// ============================================================================
// DUPLICATED LOGIC: also implemented in backend/app/public/spread.py.
// Change both and update the parity fixtures in shared/vectors/spread.json.
// Why: the public API resolves spread rates on the server (ADR 0003 amendment).
// CI runs the same fixtures in frontend/src/lib/spread.vectors.test.ts and
// backend/worker_tests/test_spread_vectors.py. A change to one side alone fails CI.
// ============================================================================
// A card and its own Customize must not be able to produce different files from the same inputs
// and the only way to guarantee that is for neither to own a copy of the arithmetic.
import {
  productColumn,
  productLbsPerAc,
  type SpreadFieldExport,
} from "./spreadExport";
import { hydrateFertRx } from "./fertSets";
import { newestSetFor, resolveSpreadRx } from "./spreadRx";
import type { SpreadPreset } from "./spreadPresets";
import type { DashboardField } from "../types/contract";

export interface SpreadRunResult {
  exports: SpreadFieldExport[];
  column: string;
  /* * fields that produced nothing, and why — surfaced rather than silently dropped */
  skipped: { fieldName: string; reason: string }[];
  /* * older-boundary and file-clip notices. The file clip is not stored on the Rx. */
  notices: { fieldName: string; message: string }[];
}

/*
 *
 * The zip FORMAT keeps its farm line; only the dead DB field stopped feeding it.
 */
export async function runSpreadPreset(
  preset: SpreadPreset,
  fields: DashboardField[],
  growerName = "",
): Promise<SpreadRunResult> {
  const nutrient = preset.nutrient;
  const column = productColumn(preset.label, preset.efficiencyPct);
  const exports: SpreadFieldExport[] = [];
  const skipped: { fieldName: string; reason: string }[] = [];
  const notices: { fieldName: string; message: string }[] = [];
  if (!nutrient) return { exports, column, skipped, notices }; // Custom has no single nutrient; it never runs this path

  for (const f of fields) {
    // The panel's `useHydratedRecSets` hydrates too, but a card click can land while that reconcile
    // is still in flight — and this run then read the per-origin cache and skipped fields as "no
    // saved Rx" for Rx the server was holding: the same bug the hydrate fixed, one action deeper.
    const set = newestSetFor(
      f.field_id,
      nutrient,
      await hydrateFertRx(f.field_id),
    );
    if (!set) {
      skipped.push({
        fieldName: f.field_name,
        reason: "no saved Rx for this nutrient",
      });
      continue;
    }
    try {
      const rx = await resolveSpreadRx(f.field_id, nutrient, set);
      const rates = rx.rates.map((v) =>
        productLbsPerAc(v, preset.efficiencyPct, nutrient),
      );
      if (!rates.some((v) => v != null)) {
        skipped.push({
          fieldName: f.field_name,
          reason: "that Rx covers no cells on this field",
        });
        continue;
      }
      // fieldId/rxId ride along for the JD send lane (addressed by ID, never name; rxId → history)
      if (rx.notice)
        notices.push({ fieldName: f.field_name, message: rx.notice });
      if (rx.fileNotice) {
        notices.push({ fieldName: f.field_name, message: rx.fileNotice });
      }
      exports.push({
        fieldId: f.field_id,
        rxId: set.id,
        fieldName: f.field_name,
        farmName: growerName,
        products: [{ column, rates }],
        geoms: rx.geoms,
      });
    } catch (e) {
      skipped.push({
        fieldName: f.field_name,
        reason: e instanceof Error ? e.message : "couldn’t read that Rx",
      });
    }
  }
  return { exports, column, skipped, notices };
}
